<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Company Announcements List" @click="$router.push('/company/announcements')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>                    
                </v-toolbar>
            </v-card>

            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encoucountered. Please contact support@sweldonow.ph. Details: {{errorMessage}}
            </v-alert>

            <!-- ANNOUNCEMENT DETAILS -->
            <v-card v-if="!loading" class="elevation-1 mt-5 mb-5">
                <v-toolbar @click="detailsVisible=!detailsVisible" class="clickable" flat height="45px">
                    <b class="primary--text">ANNOUNCEMENT DETAILS</b>
                    <v-spacer></v-spacer>
                    <v-btn icon v-if="hasEditRights && detailsVisible" @click.stop @click="showEdit" title="Edit Announcement Details">
                        <v-icon small>edit</v-icon>
                    </v-btn>
                    <v-btn icon>
                        <v-icon>{{ detailsVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-layout v-if="detailsVisible" row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                    <v-flex xs12 sm6 :class="isMobile?'mt-10':'mt-10 mb-10'">
                        <v-layout row wrap>
                            <v-flex xs12>
                                <div class="caption grey--text text--darken-1">Title</div>
                                <div v-if="annoucement.detailsLink" class="clickable underlined" @click="showDetailsLink(annoucement.detailsLink)">
                                    {{ annoucement.title }}
                                    <v-icon small class="ml-5">open_in_new</v-icon>
                                </div>
                                <div v-else>{{ annoucement.title }}</div>
                            </v-flex>
                            <v-flex xs12>
                                <div class="caption grey--text text--darken-1">Details</div>
                                <div class="mr-20">{{ annoucement.details }}</div>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                    <v-flex xs12 sm6 :class="isMobile?'mt-10':'mt-10 mb-10'">
                        <v-layout row wrap>
                            <v-flex xs12 sm6>
                                <div class="caption grey--text text--darken-1">Category</div>
                                <v-chip small class="user-chip-container" :style="`background-color:${annoucement.backgroundColor}`">
                                    <span class="white--text">{{ annoucement.categoryName }}</span>
                                </v-chip>
                            </v-flex>
                            <v-flex xs12 sm6>
                                <div class="caption grey--text text--darken-1">Type</div>
                                <v-chip small class="user-chip-container">
                                    {{ annoucement.typeName }}
                                </v-chip>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap>
                            <v-flex xs6 sm6>
                                <div class="caption grey--text text--darken-1">Event Starts</div>
                                <div>{{ formatDate(annoucement.effectiveDate) }}</div>
                            </v-flex>
                            <v-flex xs6 sm6>
                                <div class="caption grey--text text--darken-1">Event Ends</div>
                                <div>{{ formatDate(annoucement.expirationDate) }}</div>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap v-if="hasAddRights || hasEditRights">
                            <v-flex xs6 sm6>
                                <div class="caption grey--text text--darken-1">Banner Visible From</div>
                                <div>{{ formatDate(annoucement.displayStartDate) }}</div>
                            </v-flex>
                            <v-flex xs6 sm6>
                                <div class="caption grey--text text--darken-1">Banner Hidden After</div>
                                <div>{{ formatDate(annoucement.displayEndDate) }}</div>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap v-if="hasAddRights || hasEditRights">
                            <v-flex xs12 sm6>
                                <div class="caption grey--text text--darken-1">Created By</div>
                                <v-chip class="user-chip-container ml-0">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(annoucement.createdByUser.userId)"></v-img>
                                    </v-avatar>
                                    {{annoucement.createdByUser.firstName}} {{annoucement.createdByUser.lastName}}
                                </v-chip>
                            </v-flex>
                            <v-flex xs12 sm6>
                                <div class="caption grey--text text--darken-1">Created On</div>
                                <div>{{ formatDate(annoucement.createdOn) }}</div>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap v-if="hasAddRights || hasEditRights">
                            <v-flex xs12>
                                <div class="caption grey--text text--darken-1">Show Event Dates on Banner</div>
                                <div>{{ annoucement.displayDuration ? 'Yes' : 'No' }}</div>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>

                <v-divider v-if="detailsVisible"></v-divider>

                <user-reactions 
                    v-if="detailsVisible"
                    :initial-reaction-id="userReactionId" 
                    :summary-data="announcementReactionSummary"
                    @send="onReactionSubmitted"
                    />
            </v-card>

            <!-- ATTACHMENTS -->
            <v-card v-if="!loading" class="elevation-1 mt-5 mb-5">
                <v-toolbar @click="attachmentsVisible=!attachmentsVisible" class="clickable" flat height="45px">
                    <b class="primary--text">ATTACHMENTS</b>
                    <v-spacer></v-spacer>
                    <v-btn icon v-if="hasAddRights && attachmentsVisible" @click.stop @click="showUpload" title="Add Attachment">
                        <v-icon>add</v-icon>
                    </v-btn>
                    <v-btn icon>
                        <v-icon>{{ attachmentsVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-data-table
                    v-if="attachmentsVisible"
                    :headers="headerAttachments"
                    :items="annoucement.attachments"
                    :hide-actions="annoucement.attachments.length<6"
                    :loading="loading_attachment"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">
                        <tr>
                            <td @click="previewAttachment(props.item)" title="Click to preview file">
                                <div class="clickable wrapped-filename">
                                    {{ props.item.fileName }}
                                </div>
                            </td>
                            <td>{{ props.item.createdOn | moment('MMM DD, YYYY') }}</td>
                            <td v-if="hasDeleteRights">
                                <v-btn icon title="Delete Attachment" @click="showDeleteAttachment(props.item)" :disabled="loading_attachment">
                                    <v-icon small>delete</v-icon>
                                </v-btn>
                            </td> 
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

            <!-- RECIPIENTS -->
            <v-card v-if="!loading" class="elevation-1 mt-5 mb-5">
                <v-toolbar @click="recipientsVisible=!recipientsVisible" class="clickable" flat height="45px">
                    <b class="primary--text">RECIPIENTS</b>
                    <v-spacer></v-spacer>
                    <v-btn icon v-if="hasAddRights && recipientsVisible" @click.stop @click="showAddRecipient" title="Add Recipient">
                        <v-icon>add</v-icon>
                    </v-btn>
                    <v-btn icon>
                        <v-icon>{{ recipientsVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-data-table
                    v-if="recipientsVisible"
                    :headers="isMobile ? [] : headerRecipients"
                    :items="annoucement.recipients"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >  
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headerRecipients.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container ml-0">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.firstName}} {{props.item.lastName}}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                        <v-btn v-if="hasDeleteRights" icon title="Remove Recipient" @click="showRemoveRecipient(props.item)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout-mobile">
                                        <v-flex xs12 class="text-xs-left mt-1 mb-1">
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text">Teams</div>
                                                    <team-chips :teams="props.item.teams"></team-chips>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs7>
                                                    <div class="caption grey--text">Reaction</div>
                                                    <v-icon v-if="props.item.reactionTypeId" :color="getReactionType(props.item.reactionTypeId).color">
                                                        {{ getReactionType(props.item.reactionTypeId).icon }}
                                                    </v-icon>
                                                </v-flex>
                                                <v-flex xs5 v-if="props.item.reactionTypeId>0">
                                                    <div class="caption grey--text">Date Reacted</div>
                                                    <div v-if="props.item.responseDate">{{ formatCreatedDate(props.item.responseDate) }}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else>
                            <td>
                                <v-chip class="user-chip-container ml-0">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.firstName}} {{props.item.lastName}}
                                </v-chip>
                            </td>
                            <td>
                                <team-chips :teams="props.item.teams"></team-chips>
                            </td>
                            <td>
                                <v-icon v-if="props.item.reactionTypeId" :color="getReactionType(props.item.reactionTypeId).color">
                                    {{ getReactionType(props.item.reactionTypeId).icon }}
                                </v-icon>
                            </td>
                            <td>
                                <span v-if="props.item.responseDate">{{ formatCreatedDate(props.item.responseDate) }}</span>
                            </td>
                            <td v-if="hasDeleteRights">
                                <v-btn icon title="Remove Recipient" @click="showRemoveRecipient(props.item)">
                                    <v-icon small>delete</v-icon>
                                </v-btn>
                            </td>
                        </tr>

                    </template>
                </v-data-table> 
            </v-card>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="companyAnnouncementId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" persistent width="900">
            <add-edit ref="refAddEdit" :dialogAddEdit.sync="dialogAddEdit" @send="getAnnouncement(false)"></add-edit>
        </v-dialog>
        <v-dialog v-model="dialogUpload" fullscreen hide-overlay transition="dialog-bottom-transition"> 
            <upload ref="refUpload" :dialogUpload.sync="dialogUpload" @send="getAnnouncement(false)"></upload>
        </v-dialog>
        <v-dialog v-model="dialogDelete" width="500">
            <delete ref="refDeleteComponent" :okLabel="deleteOkLabel" :dialogDelete.sync="dialogDelete" @okDelete="deleteHandler"></delete>
        </v-dialog>
        <v-dialog v-model="dialogPreview" fullscreen hide-overlay transition="dialog-bottom-transition">
            <preview ref="refPdfPreviewComponet" :dialogPreview.sync="dialogPreview"></preview>
        </v-dialog>
        <v-dialog v-model="dialogAddRecipients" persistent width="600">
            <add-recipients ref="refAddRecipient" :dialogAddRecipients.sync="dialogAddRecipients" @send="getAnnouncement(false)"></add-recipients>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="companyAnnouncementId" @refresh="updateHandler"></status-messaging>
    </v-layout> 
</template>

<script>

import companyAnnouncementsApi from '@/api/companyannouncements'
import awsS3Helper from '@/helpers/aws-s3'
import { hasAddRights, hasEditRights, hasDeleteRights } from '@/helpers/access-permissions'
import { getExpandCollapseVisibility, updateExpandCollapseVisibility } from '@/helpers/user-prefs'
import { formatDate, formatCreatedDate } from '@/helpers/date-helpers'
import AWSS3DocumentType from '@/constants/aws-s3-document-types'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, updateRowsPerPage, updateSort } from '@/helpers/user-prefs'
import { getReactionType } from '@/helpers/reaction-types.js'

export default {     
    name: 'CompanyAnnouncementDetails',
    metaInfo: {
        title: 'Company Announcements Details',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Company Announcements Details' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory'),
        'team-chips': () => import("@/components/Common/TeamsChip"),
        'user-reactions': () => import("@/components/UserReactions/UserReactions"),
        'add-edit': ()=> import('@/components/CompanyAnnouncements/AddEditCompanyAnnouncement'),
        'upload': () => import('@/components/CompanyAnnouncements/AddAttachments'),
        'add-recipients': () => import('@/components/CompanyAnnouncements/AddRecipients'),
        'delete': () => import('@/components/BasicPopups/Delete'),
        'preview': () => import('@/components/FilePreview/Preview'),
    },
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            formatDate,
            formatCreatedDate,
            getReactionType,
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            detailsVisible: getExpandCollapseVisibility(routeName, 'company-announcement-details'),
            attachmentsVisible: getExpandCollapseVisibility(routeName, 'company-announcement-attachments'),
            recipientsVisible: getExpandCollapseVisibility(routeName, 'company-announcement-recipients'),                      
            dialogAddEdit: false,
            dialogUpload: false,
            dialogDelete: false,
            dialogPreview: false,
            dialogAddRecipients: false,
            loading_attachment: false,
            error: false,
            errorMessage: '',
            loading: false,
            selectedDeleteData: null,
            selectedRemoveRecipient: null,
            deleteFunction: null,
            deleteOkLabel: null,
            annoucement: null,
        }
    },
    methods: {
        async onReactionSubmitted(reactionData) {
            try {
                const data = {
                    companyAnnouncementId: this.companyAnnouncementId,
                    reactionTypeId: reactionData.reactionTypeId
                }
                const response = await companyAnnouncementsApi.addReaction(data)
                if (response && response.status == 200) {
                    this.getAnnouncement()
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async removeRecipient() {
            try {
                const data = {
                    companyAnnouncementId: this.companyAnnouncementId,
                    userId: this.selectedRemoveRecipient.userId
                }
                const response = await companyAnnouncementsApi.removeRecipient(data)
                if (response && response.status == 200) {
                    await this.getAnnouncement(false)
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showAddRecipient() {
            this.$refs.refAddRecipient.setAdd(this.companyAnnouncementId)
            this.dialogAddRecipients = true
        },
        showUpload(){
            this.$refs.refUpload.setData(this.companyAnnouncementId)
            this.dialogUpload = true
        },
        showRemoveRecipient(data) {
            this.selectedRemoveRecipient = data
            this.deleteOkLabel = 'Remove'
            this.$refs.refDeleteComponent.setMessage(`Remove ${data.firstName} ${data.lastName}?`)
            this.deleteFunction = () => this.removeRecipient()
            this.dialogDelete = true
        },
        showDeleteAttachment(data) {
            this.selectedDeleteData = data
            this.deleteOkLabel = 'Delete'
            this.$refs.refDeleteComponent.setMessage(`Delete ${data.fileName}?`)
            this.deleteFunction = () => this.deleteAttachment()
            this.dialogDelete = true
        },
        deleteHandler() {
            if (typeof this.deleteFunction === 'function') {
                this.deleteFunction()
                this.dialogDelete = false
            }
        },
        async deleteAttachment() {
            try {
                this.loading_attachment = true
                const data = {
                    companyAnnouncementAttachmentId: this.selectedDeleteData.companyAnnouncementAttachmentId,
                    companyAnnouncementId: this.companyAnnouncementId,
                    fileName: this.selectedDeleteData.fileName
                }
                const response = await companyAnnouncementsApi.deleteAttachment(data)
                if (response && response.status == 200) {
                    await this.getAnnouncement(false)
                    this.loading_attachment = false
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async previewAttachment(item) {
            try {
                this.loading_attachment = true 
                const response = await companyAnnouncementsApi.getAttachmentUrl(item.fileName)
                if (response && response.status == 200) {
                    var file = {
                        name: item.fileName,
                        type: item.fileType,
                        url: response.data.url,
                        downloadUrl: awsS3Helper.getCompanyDocumentUrl(item.fileName, AWSS3DocumentType.CompanyAnnouncement)
                    }
                    this.$refs.refPdfPreviewComponet.setParams(file)
                    this.loading_attachment = false
                    this.dialogPreview = true
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showDetailsLink(link) {
            if (link) window.open(link)
        },
        async getAnnouncement(show_loading) {
            try {
                this.loading = show_loading
                const response = await companyAnnouncementsApi.getById(this.companyAnnouncementId)
                if (response && response.status == 200) {
                    this.annoucement = response.data
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showEdit() {
            this.$refs.refAddEdit.setEdit(this.annoucement)
            this.dialogAddEdit = true
        },
        updateHandler(data) {
            console.log('updateHandler')
            console.log(data)

            //exclude the the device/user who initiates the update
            if (data != this.$store.state.userId) 
                this.getAnnouncement(false)

            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        },
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: 'Company Announcement Details',
            headerPageTitle: ''
        })

        this.getAnnouncement(true)
    },
    watch: {
        detailsVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'company-announcement-details',newVal)
        },
        attachmentsVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'company-announcement-attachments',newVal)
        },
        recipientsVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'company-announcement-recipients',newVal)
        },
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        hasDeleteRights() {
            return hasDeleteRights(this.$router.currentRoute.path)
        },
        companyAnnouncementId() {
            return this.$route.params.companyAnnouncementId
        },
        commentsData() {
            return {
                pageQueryId: this.companyAnnouncementId,
                
                //these are used to notify other users when adding comment
                //this is for refactor. see dev tasks.
                pageItemCreatedBy: null,
                pageLink: null,
                pageKey: null,
                notificationTypeId: null,
            }
        },
        headerRecipients() {
            const header = [
                { text: 'Employee', align: 'left', value: 'lastName', width: '25%', sortable: true },
                { text: 'Teams', align: 'left', value: 'teams', width: '30%', sortable: true },
                { text: 'Reaction', align: 'left', value: 'reactionTypeId', width: '15%', sortable: true },
                { text: 'Date Reacted', align: 'left', value: 'responseDate', width: '15%', sortable: true },
            ]
            if (this.hasDeleteRights) {
                header.push({ text: 'Action', align: 'center', value: '', width: '5%', sortable: false })
            }
            return header
        },
        headerAttachments() {
            const header = [
                {text: 'File',align: 'left',value: 'name', width: '70%', sortable:false},
                {text: 'Created On',align: 'left',value: 'status',  width: '25%', sortable:false}
            ]
            if (this.hasDeleteRights) {
                header.push({text: 'Action',align: 'center',value: '',  width: '5%', sortable:false})
            }
            return header
        },
        loggedInUser() {
            return this.$store.state.userId
        },
        userReactionId() {
            if (this.annoucement && this.annoucement.recipients) {
                const userReaction = this.annoucement.recipients.find(x => x.userId == this.loggedInUser)
                if (userReaction) return userReaction.reactionTypeId
            }
        },
        announcementReactionSummary() {
            // 1. Accumulate counts using a key-value reduce map
            const counts = this.annoucement.recipients.reduce((acc, recipient) => {
                const typeId = recipient.reactionTypeId
                
                // Ignore recipients who haven't left a reaction yet
                if (typeId !== null && typeId !== undefined) {
                    const numericId = parseInt(typeId);
                    acc[numericId] = (acc[numericId] || 0) + 1
                }
                
                return acc
                }, {}
            )

            // 2. Transform the map into your component's required array format
            return Object.keys(counts).map(id => ({
                id: parseInt(id),
                count: counts[id]
            }))
        }
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 25px 0px 50px;
}
.section-layout-mobile {
    padding:0px 20px 0px 20px;
}
</style>